/*
  Free gifts on the rewards ladder.

  A milestone can name a product. Cross its amount and that product is put in
  the bag on its own; drop back under and it is taken out again. The shopper
  never adds or removes it.

  What this file does NOT do is make it free. A theme cannot set a price — that
  is server-side, and has to be. The merchant creates an automatic discount in
  Shopify ("100% off <product>, minimum purchase <amount>") and this only puts
  the product where that discount can find it. Without the discount the gift is
  added at full price, which is why the setting says so in the admin.

  Gift lines carry a _tyo_gift line property holding the amount that earned
  them. Identifying them that way rather than by variant means the same product
  can still be bought normally, and it survives the merchant re-pointing a rung
  at a different product.
*/

/*
  Module-level, not per-instance: the element is destroyed and rebuilt on every
  cart render, so an instance field would forget a failed variant immediately
  and retry it on the next pass — an add that always fails would then loop
  add / fail / re-render / add for as long as the drawer is open.
*/
const unavailable = new Set();

class CartGiftWatch extends HTMLElement {
  connectedCallback() {
    if (this.checked) return;
    this.checked = true;

    let config = [];
    try {
      config = JSON.parse(this.querySelector('script[type="application/json"]')?.textContent || '[]');
    } catch (error) {
      console.error('[cart-gift] could not read the gift configuration', error);
      return;
    }

    this.config = config.filter((gift) => gift && gift.amount > 0 && gift.variant);
    if (!this.config.length) return;

    this.reconcile();
  }

  /*
    Overlapping passes are collapsed rather than dropped. A pass takes a few
    round trips, and the shopper can change a quantity inside that window —
    cart.js then rebuilds this element, whose request would otherwise be lost
    and the gift left wrong until the next thing they touched. Instead the
    request is remembered and answered once the pass in flight is done.

    It terminates because a pass that changes nothing does not re-render, and a
    gift that cannot be added is remembered as unavailable, so each pass either
    moves the bag closer to settled or ends the cycle.
  */
  async reconcile() {
    if (CartGiftWatch.busy) {
      CartGiftWatch.pending = true;
      return;
    }
    CartGiftWatch.busy = true;

    try {
      const cart = await this.readCart();
      if (!cart) return;

      /*
        The gifts' own prices are excluded, for the reason in the Liquid: a gift
        that counted toward its own threshold would keep the bag oscillating
        across the line. final_line_price rather than original, so an ordinary
        discount the shopper earned still counts toward the next rung.
      */
      const earned = cart.items
        .filter((line) => !this.tagOf(line))
        .reduce((sum, line) => sum + line.final_line_price, 0);

      let changed = false;

      /*
        Removals first. Doing it in this order means a rung that has been
        re-pointed at a different product swaps cleanly in one pass instead of
        briefly holding both.
      */
      for (const line of cart.items) {
        const tag = this.tagOf(line);
        if (!tag) continue;

        const keep = this.config.some(
          (gift) => String(gift.amount) === tag && gift.variant === line.variant_id && earned >= gift.amount
        );

        if (!keep) {
          changed = (await this.setQuantity(line.key, 0)) || changed;
        } else if (line.quantity !== 1) {
          // A gift is one per bag. Nothing in the UI offers to change it, but a
          // stale tab or a hand-made request can.
          changed = (await this.setQuantity(line.key, 1)) || changed;
        }
      }

      // Then anything newly earned.
      for (const gift of this.config) {
        if (earned < gift.amount) continue;
        if (unavailable.has(gift.variant)) continue;

        const present = cart.items.some((line) => this.tagOf(line) === String(gift.amount));
        if (present) continue;

        changed = (await this.add(gift)) || changed;
      }

      /*
        Only re-render when something actually moved. The render rebuilds this
        element, which runs reconcile again — that second pass finding nothing
        to do is what ends the cycle, and refreshing unconditionally would mean
        it never did.
      */
      if (changed) await this.refresh();
    } catch (error) {
      console.error('[cart-gift] could not settle the free gifts', error);
    } finally {
      CartGiftWatch.busy = false;
    }

    if (CartGiftWatch.pending) {
      CartGiftWatch.pending = false;
      await this.reconcile();
    }
  }

  tagOf(line) {
    const value = line.properties && line.properties._tyo_gift;
    return value ? String(value) : '';
  }

  async readCart() {
    const response = await fetch(`${window.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
    if (!response.ok) return null;
    return response.json();
  }

  async add(gift) {
    const response = await fetch(window.routes.cart_add_url, {
      ...fetchConfig('javascript'),
      body: JSON.stringify({
        items: [{ id: gift.variant, quantity: 1, properties: { _tyo_gift: String(gift.amount) } }],
      }),
    });
    const data = await response.json();

    if (data.status) {
      /*
        Sold out, unpublished, or deleted. Remembered so the next pass skips it
        rather than trying again — the shopper is not waiting on this and has
        no way to fix it, so it stays quiet in the console.
      */
      unavailable.add(gift.variant);
      console.warn('[cart-gift] the free gift could not be added', data.description || data.message);
      return false;
    }

    return true;
  }

  async setQuantity(key, quantity) {
    const response = await fetch(window.routes.cart_change_url, {
      ...fetchConfig('javascript'),
      body: JSON.stringify({ id: key, quantity }),
    });
    if (!response.ok) {
      console.warn('[cart-gift] could not adjust a gift line');
      return false;
    }
    return true;
  }

  /*
    The drawer's own refreshFromServer opens the drawer when it finishes, which
    is right for an add the shopper asked for and wrong for this one — a gift
    appearing should not throw the bag open over the page they are reading. So
    the same swap, without the open.
  */
  async refresh() {
    const response = await fetch(`${window.location.pathname}?sections=cart-drawer,cart-icon-bubble`);
    if (!response.ok) return;
    const sections = await response.json();

    const parse = (html, selector) =>
      new DOMParser().parseFromString(html || '', 'text/html').querySelector(selector);

    const bubble = document.getElementById('cart-icon-bubble');
    const freshBubble = parse(sections['cart-icon-bubble'], '.shopify-section');
    if (bubble && freshBubble) bubble.innerHTML = freshBubble.innerHTML;

    const drawer = document.getElementById('CartDrawer');
    const freshDrawer = parse(sections['cart-drawer'], '#CartDrawer');
    if (drawer && freshDrawer) drawer.innerHTML = freshDrawer.innerHTML;

    document.querySelector('cart-drawer')?.classList.toggle('is-empty', !!document.querySelector('.drawer__inner-empty'));
  }
}

CartGiftWatch.busy = false;
CartGiftWatch.pending = false;

if (!customElements.get('cart-gift-watch')) customElements.define('cart-gift-watch', CartGiftWatch);
